/* ============================================================================
   MOCK DATA + MOCK AUTH — design build only, nothing here talks to a service.
   ----------------------------------------------------------------------------
   ponytail: the whole module is a stand-in so the journey is clickable before
   the backend is wired. Ceiling: OTPs are never sent, the code is hardcoded,
   and the "session" is a sessionStorage flag with no token — it proves nothing
   about who the candidate is. Upgrade path (developer handoff):

     requestOtp(email)   → POST {AUTH_API_URL}/…/send-otp   via a BFF route
                           handler under src/app/api/, using src/lib/api/authService
     verifyOtp(email,otp)→ POST {AUTH_API_URL}/…/verify-otp → { token }
                           → src/lib/auth.ts setToken(token)  (v1 reads the same key)
     listAssignments()   → GET  {STD_API_URL}/…/assessments/mine via
                           src/lib/api/studentNode, Authorization: <token>
                           → Assignment[] in take-order

   Shapes below mirror what those endpoints should return, so swapping the
   implementation should not touch a single screen.
   ========================================================================== */

export type AssessmentType =
  | "Aptitude"
  | "Communication"
  | "Role Based"
  | "AI Interview"
  | "Custom"
  | "Behaviour";

export type Assignment = {
  id: string;
  type: AssessmentType;
  title: string;
  /** Total questions across every subsection — mirrors exam.ts's content. */
  questions: number;
  durationMinutes: number;
};

/** The single test these assignments belong to — mirrors exam.ts's MOCK_TEST.name. */
export const TEST_NAME = "Campus Hiring — Batch 2026";

/** Scenarios the screens can be reviewed against: /assessment?mock=<key>. */
export type MockScenario = "multi" | "single" | "empty" | "error";

const SCENARIOS: Record<"multi" | "single", Assignment[]> = {
  multi: [
    { id: "asm-apt-01", type: "Aptitude", title: "Aptitude", questions: 10, durationMinutes: 30 },
    { id: "asm-comm-01", type: "Communication", title: "Communication", questions: 13, durationMinutes: 45 },
    { id: "asm-role-01", type: "Role Based", title: "Role Based", questions: 7, durationMinutes: 35 },
    { id: "asm-ai-01", type: "AI Interview", title: "AI Interview", questions: 4, durationMinutes: 30 },
    { id: "asm-custom-01", type: "Custom", title: "Custom", questions: 4, durationMinutes: 20 },
  ],
  single: [
    { id: "asm-apt-01", type: "Aptitude", title: "Aptitude", questions: 10, durationMinutes: 30 },
  ],
};

export function isMockScenario(v: string | null): v is MockScenario {
  return v === "multi" || v === "single" || v === "empty" || v === "error";
}

const delay = (ms: number) => new Promise((r) => setTimeout(r, ms));

/** Stands in for GET /assessments/mine — resolves in take-order. */
export async function listAssignments(
  scenario: MockScenario = "multi"
): Promise<Assignment[]> {
  await delay(650);
  if (scenario === "error") throw new Error("mock: assignments unavailable");
  if (scenario === "empty") return [];
  return SCENARIOS[scenario];
}

/* ---------------------------------------------------------------- auth ---- */

export const DEMO_OTP = "123456";
/** Demo validity window; the real one comes from the auth service. */
export const OTP_TTL_MS = 90_000;
export const RESEND_COOLDOWN_S = 30;

export const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/** Stands in for POST /auth/request-otp. */
export async function requestOtp(email: string): Promise<{ sentAt: number }> {
  if (!EMAIL_RE.test(email)) throw new Error("invalid-email");
  await delay(550);
  return { sentAt: Date.now() };
}

export type OtpFailure = "expired" | "wrong";

/** Stands in for POST /auth/verify-otp → { token }. */
export async function verifyOtp(
  code: string,
  sentAt: number
): Promise<OtpFailure | null> {
  await delay(550);
  if (Date.now() > sentAt + OTP_TTL_MS) return "expired";
  if (code !== DEMO_OTP) return "wrong";
  return null;
}

/* ------------------------------------------------------------- session ---- */

const SESSION_KEY = "pl.v2.candidate";
const ENTITY_KEY = "pl.v2.candidate.entity";

/** Who floated this attempt — corporate never shares a result with the
 *  candidate, institute does. Drives the completion screen's copy. */
export type CandidateEntityType = "corporate" | "institute";

/**
 * ponytail: a sessionStorage breadcrumb, NOT a session. It only keeps the
 * assessment screen from rendering for someone who never signed in during this
 * tab. Upgrade path: the real token from verify-otp, stored via
 * src/lib/auth.ts `setToken` (same key v1 reads), with the screens gated on it.
 *
 * DESIGN-ONLY: `entityType` is not yet real. Production has the signal — an
 * invite-token session is corporate, `openStudentSession` is institute (see
 * README: institute students never get an invite) — but v2's client never
 * carries it, the same gap `endTime` had before it was threaded through
 * `resolveInvite` (see kb/Assessment/otp-invite.md). Wiring it for real means
 * student-node's resolve/session responses returning an entity field and
 * `INVITE_SESSION_KEYS` gaining a slot for it — out of scope for a design
 * preview. Defaults to "institute" so today's copy is unchanged until that
 * lands; override with `?entity=corporate` on the demo sign-in for preview.
 */
export function startCandidateSession(email: string, entityType: CandidateEntityType = "institute"): void {
  try {
    sessionStorage.setItem(SESSION_KEY, email);
    sessionStorage.setItem(ENTITY_KEY, entityType);
  } catch {}
}

export function getCandidateEmail(): string | null {
  try {
    return sessionStorage.getItem(SESSION_KEY);
  } catch {
    return null;
  }
}

export function getCandidateEntityType(): CandidateEntityType {
  try {
    return sessionStorage.getItem(ENTITY_KEY) === "corporate" ? "corporate" : "institute";
  } catch {
    return "institute";
  }
}

export function endCandidateSession(): void {
  try {
    sessionStorage.removeItem(SESSION_KEY);
    sessionStorage.removeItem(ENTITY_KEY);
  } catch {}
}
